<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1 class="headline font-weight-medium">
          Create Custom Booking Request
        </h1>
        <v-spacer />
        <v-btn color="primary" depressed to="/bookings">Back to Bookings</v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-alert v-if="error" type="error" outlined text>{{ error }}</v-alert>

        <v-alert v-if="userAccount" type="success" outlined text>
          Attached to:
          <strong>{{ userAccount.firstName }} {{ userAccount.lastName }}</strong>
          — {{ userAccount.email }}
          <span class="ml-3">PUID: <strong>{{ puid }}</strong></span>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined :loading="loading">
          <v-card-text class="text--primary">
            <v-form ref="form" @submit.prevent="onSubmit">
              <v-row>
                <!-- Prefilled traveler info -->
                <v-col cols="12" md="4">
                  <v-text-field
                    label="First Name"
                    v-model="booking.firstName"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Last Name"
                    v-model="booking.lastName"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Primary Email"
                    v-model="booking.email"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Secondary Email"
                    v-model="booking.secondaryEmail"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Phone"
                    v-model="booking.phone"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12">
                  <v-divider class="my-4" />
                </v-col>

                <!-- Read-only package info from specific winner -->
                <v-col cols="12" md="3">
                  <v-text-field
                    label="Package Destination"
                    v-model="packageInfo.destination"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Package Code"
                    v-model="packageInfo.packageCode"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Travel By Date"
                    v-model="packageInfo.travelByDate"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Cost To Org"
                    v-model="packageInfo.costToOrg"
                    dense
                    outlined
                    disabled
                  />
                </v-col>

                <v-col cols="12">
                  <v-divider class="my-4" />
                </v-col>

                <!-- Manual entry -->
                <v-col cols="12" md="4">
                  <v-text-field
                    label="Destination *"
                    v-model.trim="booking.destination"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Resort Name *"
                    v-model.trim="booking.resortName"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Resort Address *"
                    v-model.trim="booking.resortAddress"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Check-In Date * (YYYY-MM-DD)"
                    v-model.trim="booking.checkInDate"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Check-Out Date * (YYYY-MM-DD)"
                    v-model.trim="booking.checkOutDate"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    label="Bedrooms *"
                    v-model.trim="booking.bedrooms"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    label="Max Occupancy *"
                    v-model.trim="booking.maxOccupancy"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    label="Adults *"
                    v-model.trim="booking.numAdults"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    label="Children *"
                    v-model.trim="booking.numChildren"
                    dense
                    outlined
                    :rules="[req]"
                  />
                </v-col>

                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-checkbox
                    label="Handicap Required"
                    v-model="booking.handicapRequired"
                    dense
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Kitchen"
                    v-model.trim="booking.kitchen"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    label="Supplier/Source"
                    v-model.trim="booking.source"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12">
                  <v-divider class="my-4" />
                </v-col>

                <!-- Pricing / admin fields -->
                <v-col cols="12" md="3">
                  <v-text-field
                    label="Room Cost"
                    v-model.trim="booking.roomCost"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Commissions Paid"
                    v-model.trim="booking.commissionsPaid"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Taxes & Fees"
                    v-model.trim="booking.taxesAndFees"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Total Cost"
                    v-model.trim="booking.totalCost"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Total Income"
                    v-model.trim="booking.totalIncome"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Net Profit"
                    v-model.trim="booking.netProfit"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Peak Season Surcharge 1"
                    v-model.trim="booking.peakSeasonSurchargeAmount"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Peak Season Surcharge 2"
                    v-model.trim="booking.peakSeason2SurchargeAmount"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="High Occupancy Surcharge"
                    v-model.trim="booking.highOccupancySurchargeAmount"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Third Year Surcharge"
                    v-model.trim="booking.thirdYearSurcharge"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Garden Unit Upgrade"
                    v-model.trim="booking.gardenUnitUpgrade"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Tours / Tickets"
                    v-model.trim="booking.toursTickets"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Total Surcharges"
                    v-model.trim="booking.totalSurcharges"
                    dense
                    outlined
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    label="Number of Nights"
                    v-model.trim="booking.numberOfNights"
                    dense
                    outlined
                    disabled
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn
              color="success"
              depressed
              @click="onSubmit"
              :loading="loading"
            >
              Create Request
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import firebase from "@/plugins/firebase";

const userAccounts = firebase.firestore().collection("userAccounts");
const bookings = firebase.firestore().collection("bookingsConfirmations");
const eventReports = firebase.firestore().collection("eventReports");

export default {
  data: () => ({
    loading: false,
    error: "",
    puid: "",
    userAccount: null,
    eventReport: null,
    specificWinner: null,
    req: (v) => !!(v || "").toString().trim() || "Required",

    packageInfo: {
      destination: "",
      packageCode: "",
      travelByDate: "",
      costToOrg: "",
    },

    booking: {
      userID: "",
      packageID: "",
      packageCode: "",
      travelByDate: "",

      firstName: "",
      lastName: "",
      email: "",
      secondaryEmail: "",
      phone: "",

      destination: "",
      resortName: "",
      resortAddress: "",
      checkInDate: "",
      checkOutDate: "",
      bedrooms: "",
      maxOccupancy: "",
      numAdults: "",
      numChildren: "",
      handicapRequired: false,
      kitchen: "",
      source: "",

      costToOrg: "",
      roomCost: "",
      commissionsPaid: "",
      taxesAndFees: "",
      totalCost: "",
      totalIncome: "",
      netProfit: "",
      totalSurcharges: "",
      toursTickets: "",
      highOccupancySurchargeAmount: "",
      peakSeasonSurchargeAmount: "",
      peakSeason2SurchargeAmount: "",
      thirdYearSurcharge: "",
      gardenUnitUpgrade: "",
      numberOfNights: "",

      isConfirmed: false,
      archived: false,
      dateConfirmed: "",
      confirmationNumber: "",
      dateSelectedForBooking: "",
      adminNotes: "",
      travelerNotes: "",
      pendingAVSheetCreation: false,

      assignedToUid: "",
      assignedToName: "",
      assumedByUid: "",
      assumedByName: "",
    },
  }),

  created() {
    this.loadFromRoute();
  },

  watch: {
    "$route.query.ua": function() {
      this.loadFromRoute();
    },
    "$route.query.puid": function() {
      this.loadFromRoute();
    },
    "booking.checkInDate": function() {
      this.updateNumberOfNights();
    },
    "booking.checkOutDate": function() {
      this.updateNumberOfNights();
    },
  },

  methods: {
    resetBookingState() {
      this.error = "";
      this.userAccount = null;
      this.eventReport = null;
      this.specificWinner = null;
      this.packageInfo = {
        destination: "",
        packageCode: "",
        travelByDate: "",
        costToOrg: "",
      };

      this.booking.userID = "";
      this.booking.packageID = "";
      this.booking.packageCode = "";
      this.booking.travelByDate = "";

      this.booking.firstName = "";
      this.booking.lastName = "";
      this.booking.email = "";
      this.booking.secondaryEmail = "";
      this.booking.phone = "";

      this.booking.destination = "";
      this.booking.resortName = "";
      this.booking.resortAddress = "";
      this.booking.checkInDate = "";
      this.booking.checkOutDate = "";
      this.booking.bedrooms = "";
      this.booking.maxOccupancy = "";
      this.booking.numAdults = "";
      this.booking.numChildren = "";
      this.booking.handicapRequired = false;
      this.booking.kitchen = "";
      this.booking.source = "";

      this.booking.costToOrg = "";
      this.booking.roomCost = "";
      this.booking.commissionsPaid = "";
      this.booking.taxesAndFees = "";
      this.booking.totalCost = "";
      this.booking.totalIncome = "";
      this.booking.netProfit = "";
      this.booking.totalSurcharges = "";
      this.booking.toursTickets = "";
      this.booking.highOccupancySurchargeAmount = "";
      this.booking.peakSeasonSurchargeAmount = "";
      this.booking.peakSeason2SurchargeAmount = "";
      this.booking.thirdYearSurcharge = "";
      this.booking.gardenUnitUpgrade = "";
      this.booking.numberOfNights = "";

      this.booking.isConfirmed = false;
      this.booking.archived = false;
      this.booking.dateConfirmed = "";
      this.booking.confirmationNumber = "";
      this.booking.dateSelectedForBooking = "";
      this.booking.adminNotes = "";
      this.booking.travelerNotes = "";
      this.booking.pendingAVSheetCreation = false;

      this.booking.assignedToUid = "";
      this.booking.assignedToName = "";
      this.booking.assumedByUid = "";
      this.booking.assumedByName = "";
    },

    async loadFromRoute() {
      const uaId = (this.$route.query.ua || "").toString().trim();
      const routePuid = (this.$route.query.puid || "").toString().trim();

      this.resetBookingState();
      this.puid = routePuid || "";

      if (!uaId && !routePuid) {
        this.error = "Invalid UA";
        return;
      }

      this.loading = true;

      try {
        let uaDoc = null;

        // Prefer doc id
        if (uaId) {
          const snap = await userAccounts.doc(uaId).get();
          if (snap.exists) {
            uaDoc = { id: snap.id, ...snap.data() };
          }
        }

        // Fallback by packageID
        if (!uaDoc && routePuid) {
          const candidates = Array.from(
            new Set([routePuid, routePuid.toLowerCase(), routePuid.toUpperCase()])
          );

          for (const candidate of candidates) {
            const uaSnap = await userAccounts
              .where("packageID", "==", candidate)
              .limit(1)
              .get();

            if (!uaSnap.empty) {
              const d = uaSnap.docs[0];
              uaDoc = { id: d.id, ...d.data() };
              break;
            }
          }
        }

        if (!uaDoc) {
          this.error = "This Package Is Not Registered";
          return;
        }

        this.userAccount = uaDoc;

        // canonical ids / traveler info
        this.booking.userID = uaDoc.id;
        this.puid = uaDoc.packageID || this.puid || "";
        this.booking.packageID = this.puid;

        this.booking.firstName = uaDoc.firstName || "";
        this.booking.lastName = uaDoc.lastName || "";
        this.booking.email = (uaDoc.email || "").toLowerCase();
        this.booking.secondaryEmail = (uaDoc.secondaryEmail || "").toLowerCase();
        this.booking.phone = uaDoc.phone || "";

        this.booking.assignedToUid = uaDoc.assignedToUid || "";
        this.booking.assignedToName = uaDoc.assignedToName || "";
        this.booking.assumedByUid = uaDoc.assumedByUid || "";
        this.booking.assumedByName = uaDoc.assumedByName || "";

        // Event report / specific winner
        if (uaDoc.eventReportID) {
          const eventReportSnap = await eventReports.doc(uaDoc.eventReportID).get();

          if (eventReportSnap.exists) {
            const eventReport = {
              id: eventReportSnap.id,
              ...eventReportSnap.data(),
            };
            this.eventReport = eventReport;

            const winners = Array.isArray(eventReport.winners)
              ? eventReport.winners
              : [];

            const specificWinner =
              winners.find((i) => i.packageID === uaDoc.packageID) || null;

            this.specificWinner = specificWinner;

            if (specificWinner) {
              this.packageInfo.destination = specificWinner.destination || "";
              this.packageInfo.packageCode = specificWinner.packageCode || "";
              this.packageInfo.travelByDate =
                specificWinner.travelByDate || specificWinner.travelBy || "";

              if (
                specificWinner.costToOrg !== undefined &&
                specificWinner.costToOrg !== null
              ) {
                this.packageInfo.costToOrg = specificWinner.costToOrg;
              } else if (
                specificWinner.actualCostToOrg !== undefined &&
                specificWinner.actualCostToOrg !== null
              ) {
                this.packageInfo.costToOrg = specificWinner.actualCostToOrg;
              } else if (
                specificWinner.cost !== undefined &&
                specificWinner.cost !== null
              ) {
                this.packageInfo.costToOrg = specificWinner.cost;
              } else {
                this.packageInfo.costToOrg = "";
              }

              // default editable destination from winner
              if (!this.booking.destination) {
                this.booking.destination = this.packageInfo.destination || "";
              }
            }
          }
        }
      } catch (e) {
        console.error("[CustomBooking:new] loadFromRoute error:", e);
        this.error = "Invalid UA";
      } finally {
        this.loading = false;
      }
    },

    updateNumberOfNights() {
      if (!this.booking.checkInDate || !this.booking.checkOutDate) {
        this.booking.numberOfNights = "";
        return;
      }

      const checkIn = new Date(this.booking.checkInDate);
      const checkOut = new Date(this.booking.checkOutDate);

      if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
        this.booking.numberOfNights = "";
        return;
      }

      const diff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      this.booking.numberOfNights = diff > 0 ? diff : 0;
    },

    todayISO() {
      return new Date().toISOString().substring(0, 10);
    },

    formatDowMmmDdYYYY(dateObj) {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const d = dateObj.getDate();
      const dd = d < 10 ? "0" + d : "" + d;

      return `${days[dateObj.getDay()]} ${months[dateObj.getMonth()]} ${dd}, ${dateObj.getFullYear()}`;
    },

    buildCustomRequestTimelineItem() {
      const now = new Date();
      const pretty = this.formatDowMmmDdYYYY(now);

      return {
        adminActionRequired: false,
        archived: false,
        dateCreated: this.todayISO(),
        from: "admin",
        message:
          `Custom Travel Request Created Date: ${pretty} ` +
          `Please do not make additional travel arrangements until you receive a confirmation from us stating that it is safe to do so.`,
        readByAdmin: true,
        repliedToByAdmin: true,
        senderActionRequired: true,
        title: "Custom Travel Request Created",
        to: this.userAccount && this.userAccount.email ? this.userAccount.email : "",
        icon: "mdi-map-marker-radius-outline",
        color: "warning",
      };
    },

    toNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    },

    async onSubmit() {
      this.error = "";
      if (!this.$refs.form || !this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const payload = {
          ...this.booking,
          dateSelectedForBooking: this.todayISO(),
        };

        // copy package info into booking doc
        payload.packageCode = this.packageInfo.packageCode || payload.packageCode || "";
        payload.travelByDate = this.packageInfo.travelByDate || payload.travelByDate || "";
        payload.costToOrg =
          this.packageInfo.costToOrg !== ""
            ? this.toNumber(this.packageInfo.costToOrg)
            : this.toNumber(payload.costToOrg);

        // normalize numeric fields so _id.vue never receives undefined
        payload.roomCost = this.toNumber(payload.roomCost);
        payload.commissionsPaid = this.toNumber(payload.commissionsPaid);
        payload.taxesAndFees = this.toNumber(payload.taxesAndFees);
        payload.totalCost = this.toNumber(payload.totalCost);
        payload.totalIncome = this.toNumber(payload.totalIncome);
        payload.netProfit = this.toNumber(payload.netProfit);
        payload.totalSurcharges = this.toNumber(payload.totalSurcharges);
        payload.toursTickets = this.toNumber(payload.toursTickets);
        payload.highOccupancySurchargeAmount = this.toNumber(
          payload.highOccupancySurchargeAmount
        );
        payload.peakSeasonSurchargeAmount = this.toNumber(
          payload.peakSeasonSurchargeAmount
        );
        payload.peakSeason2SurchargeAmount = this.toNumber(
          payload.peakSeason2SurchargeAmount
        );
        payload.thirdYearSurcharge = this.toNumber(payload.thirdYearSurcharge);
        payload.gardenUnitUpgrade = this.toNumber(payload.gardenUnitUpgrade);
        payload.numberOfNights = this.toNumber(payload.numberOfNights);

        // keep totalSurcharges sane if user didn't type it
        if (!payload.totalSurcharges) {
          payload.totalSurcharges =
            payload.highOccupancySurchargeAmount +
            payload.peakSeasonSurchargeAmount +
            payload.peakSeason2SurchargeAmount +
            payload.thirdYearSurcharge +
            payload.gardenUnitUpgrade;
        }

        const currentUser =
          this.$store && this.$store.state && this.$store.state.user
            ? this.$store.state.user
            : null;

        if (currentUser && currentUser.uid) {
          payload.assignedToUid = currentUser.uid;
        }

        payload.userID = this.userAccount ? this.userAccount.id : payload.userID;

        // deterministic doc id to match normal traveler flow
        const nextSelectionNumber =
          Number(this.userAccount.numberOfInventorySelections || 0) + 1;

        const docID = `${payload.packageID}-${nextSelectionNumber}`;

        await bookings.doc(docID).set(payload, { merge: true });

        const timelineItem = this.buildCustomRequestTimelineItem();
        timelineItem.bookingConfirmationID = docID;

        await userAccounts.doc(payload.userID).update({
          canBook: false,
          canBrowseInventory: false,
          pendingConfirmation: true,
          pendingAVSheetCreation: false,
          timeline: firebase.firestore.FieldValue.arrayUnion(timelineItem),
          numberOfInventorySelections: firebase.firestore.FieldValue.increment(1),
          dateInventorySelected: this.todayISO(),
          inventorySelections: firebase.firestore.FieldValue.arrayUnion(docID),
          hasSelectedInventory: true,
        });

        await axios.post("/api/custom-booking-request-created", {
          userAccount: this.userAccount,
          bookingData: payload,
          bookingID: docID,
        });

        this.$router.push(`/bookings/${docID}`);
      } catch (e) {
        console.error(e);
        this.error = "Unable to create request";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>